import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, TemplateRef, ViewChild } from "@angular/core";
import { ActivatedRoute, Router } from "@angular/router";
import { isEmpty } from "lodash";
import moment from "moment";
import { Columns, Config, DefaultConfig, Pagination } from "ngx-easy-table";
import { NgxIndexedDBService } from "ngx-indexed-db";
import { Observable, Subscription, forkJoin, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";

import { ETRAINING_DB } from "src/app/app.module";
import { CONSTANT } from "src/app/constant";
import { RouteType } from "src/app/models/enum";
import { FormParams, IndexedDb, PaginationDetails, TrainingForms } from "src/app/models/models";
import { DateRange } from "src/app/shared/constants/angular-datetime-picker.const";
import { HttpService } from "src/app/shared/services/http.service";
import { LoaderService } from "src/app/shared/services/loader.service";
import { RoleService } from "src/app/shared/services/role.service";
import { SharedService } from "src/app/shared/services/shared.service";
import { getFormTypeShortLabel } from "src/app/shared/utils/form-type-label";
import { isFormTypeVisibleToRole } from "src/app/shared/utils/form-types-visible.utils";
import { defaultListDateRange } from "src/app/shared/utils/list-date-filter.utils";

@Component({
	selector: "app-ratings",
	templateUrl: "./ratings.component.html",
	styleUrls: ["./ratings.component.scss"],
	changeDetection: ChangeDetectionStrategy.Eager,
	standalone: false,
})
export class RatingsComponent implements OnInit, OnDestroy {
	public columns!: Columns[];
	public configuration!: Config;
	isOnline = true;

	@ViewChild("trainingNameTpl", { static: true })
	trainingNameTpl!: TemplateRef<any>;
	@ViewChild("formTypeTpl", { static: true }) formTypeTpl!: TemplateRef<any>;
	@ViewChild("actionTpl", { static: true }) actionTpl!: TemplateRef<any>;
	@ViewChild("dateTpl", { static: true }) dateTpl!: TemplateRef<any>;
	@ViewChild("sessionTpl", { static: true }) sessionTpl!: TemplateRef<any>;

	filters?: FormParams = {
		page: this.constant.PAGINATION.DEFAULT_PAGE,
		size: this.constant.PAGINATION.DEFAULT_SIZE,
	};

	public pagination: Pagination = {
		limit: this.constant.PAGINATION.DEFAULT_SIZE,
		offset: 1,
		count: 1,
	};

	formsSub!: Subscription;
	routeSub!: Subscription;

	forms: TrainingForms[] = [];
	private readonly initialRange = defaultListDateRange();
	startDate = this.initialRange.startDate;
	endDate = this.initialRange.endDate;
	dateFilterEnabled = false;

	constructor(
		private sharedService: SharedService,
		private router: Router,
		private roleService: RoleService,
		private httpService: HttpService,
		private route: ActivatedRoute,
		private loaderService: LoaderService,

		// eTrainingDB via token
		@Inject(ETRAINING_DB) private eTrainingDb: NgxIndexedDBService,
	) {
		const params = this.route.snapshot.queryParams;
		if (params["startDate"] && params["endDate"]) {
			this.dateFilterEnabled = true;
			this.startDate = moment(params["startDate"]).toDate();
			this.endDate = moment(params["endDate"]).toDate();
		}
	}

	ngOnInit(): void {
		this.sharedService.getIsStatusOnline().subscribe((status: boolean) => {
			this.isOnline = status;
		});

		this.sharedService.setActivePage(RouteType.RATINGS);
		this.initTableConfig();
		this.initParams();
	}

	ngOnDestroy(): void {
		if (this.formsSub) this.formsSub.unsubscribe();
		if (this.routeSub) this.routeSub.unsubscribe();
	}

	// ---------------- Table setup ----------------
	initTableConfig() {
		this.configuration = { ...DefaultConfig };
		this.configuration.searchEnabled = false;
		this.configuration.tableLayout.style = "bigger";
		this.configuration.paginationMaxSize = this.constant.PAGINATION.MAX_PAGINATION_COUNT;

		this.columns = [
			{
				key: "formTypeLabel",
				title: "Form Type",
				cellTemplate: this.formTypeTpl,
				width: "18%",
			},
			{
				key: "name",
				title: "Test Name",
				cellTemplate: this.trainingNameTpl,
				width: "22%",
			},
			{
				key: "sessionConfig",
				title: "Session Details",
				cellTemplate: this.sessionTpl,
				width: "25%",
			},
			{
				key: "updatedDate",
				title: "Last Updated",
				cellTemplate: this.dateTpl,
				width: "20%",
			},
			{
				key: "",
				title: "Action",
				cellTemplate: this.actionTpl,
				width: "15%",
			},
		];
	}

	initParams(): void {
		this.routeSub = this.route.queryParams.subscribe((params) => {
			const hasDates = Boolean(params["startDate"] && params["endDate"]);
			this.dateFilterEnabled = hasDates;
			this.updateFilters({
				...this.filters,
				...params,
				...(!hasDates ? { startDate: undefined, endDate: undefined } : {}),
			});
		});
	}

	// ---------------- DB helpers ----------------
	/** Upsert the current server page into eTrainingDB for offline rating access. */
	private cacheServerForms(serverForms: TrainingForms[]): Observable<void> {
		if (!serverForms?.length) {
			return of(undefined);
		}

		const upserts = serverForms.map((form) => {
			const record = {
				id: String((form as any).id),
				data: form,
				stringify: JSON.stringify(form),
			};

			return this.eTrainingDb.update(CONSTANT.INDEXEDDB.FORM, record).pipe(
				catchError(() => this.eTrainingDb.add(CONSTANT.INDEXEDDB.FORM, record)),
				catchError((err) => {
					console.error(`Failed to cache form ${record.id} in eTrainingDB:`, err);
					return of(null);
				}),
			);
		});

		return forkJoin(upserts).pipe(map(() => undefined));
	}

	// ---------------- Local reads + merging ----------------
	private fetchLocalForms(db: NgxIndexedDBService) {
		return db.getAll<IndexedDb<TrainingForms>>(CONSTANT.INDEXEDDB.FORM).pipe(
			map((rows) => rows.map((r) => r.data)),
			catchError((err) => {
				console.error("IndexedDB read failed:", err);
				return of<TrainingForms[]>([]);
			}),
		);
	}

	private filterFormsByRole(forms: TrainingForms[]): TrainingForms[] {
		const role = this.roleService.getUserRole();
		return forms.filter((form) => {
			const formTypeCode = form.formType?.code ?? form.formTypeCode ?? "";
			return isFormTypeVisibleToRole(formTypeCode, role);
		});
	}

	private mergeForms(...lists: TrainingForms[][]): TrainingForms[] {
		const byId = new Map<string, TrainingForms>();
		for (const list of lists) {
			for (const f of list ?? []) {
				byId.set(String((f as any).id), f); // last write wins
			}
		}
		// optional sort by updatedDate desc
		return this.filterFormsByRole(
			Array.from(byId.values()).sort((a: any, b: any) => {
				const ad = a?.updatedDate ? new Date(a.updatedDate).getTime() : 0;
				const bd = b?.updatedDate ? new Date(b.updatedDate).getTime() : 0;
				return bd - ad;
			}),
		);
	}

	// ---------------- Load list ----------------
	initFormList(): void {
		this.formsSub?.unsubscribe();
		this.loaderService.setLoading(true);
		const online = this.isOnline && navigator.onLine;

		if (!online) {
			// OFFLINE: load from eTrainingDB
			this.formsSub = this.fetchLocalForms(this.eTrainingDb).subscribe({
				next: (localForms) => {
					const merged = this.mergeForms(localForms);
					this.forms = this.withFormTypeLabels(merged);

					const total = merged.length;
					const paginationDetails = this.calculatePages(total, this.size);
					this.pagination = {
						limit: this.size,
						offset: this.page,
						count: this.size * paginationDetails.totalPages,
					};

					this.loaderService.setLoading(false);
				},
				error: (error) => {
					console.error("Offline merge failed:", error);
					this.loaderService.setLoading(false);
				},
			});

			return;
		}

		// ONLINE: server page drives the table; IndexedDB is updated for offline use only
		this.formsSub = this.httpService
			.instructorFormsRating(this.filters)
			.pipe(
				switchMap((result): Observable<{ serverForms: TrainingForms[]; total: number }> => {
					const data = result.data;
					const serverForms = (data.data as TrainingForms[]) ?? [];
					const total: number = data.total ?? serverForms.length;

					return this.cacheServerForms(serverForms).pipe(map(() => ({ serverForms, total })));
				}),
				catchError((error) => {
					console.error("API call failed, falling back to local data:", error);
					return this.fetchLocalForms(this.eTrainingDb).pipe(
						map((localForms) => ({
							serverForms: this.mergeForms(localForms),
							total: localForms.length,
						})),
					);
				}),
			)
			.subscribe({
				next: ({ serverForms, total }) => {
					this.forms = this.withFormTypeLabels(serverForms);

					const paginationDetails = this.calculatePages(total, this.size);
					this.pagination = {
						limit: this.size,
						offset: this.page,
						count: this.size * paginationDetails.totalPages,
					};

					this.loaderService.setLoading(false);
				},
				error: (error) => {
					console.error("Online load failed:", error);
					this.fetchLocalForms(this.eTrainingDb).subscribe({
						next: (localForms) => {
							const merged = this.mergeForms(localForms);
							this.forms = this.withFormTypeLabels(merged);
							const total = merged.length;
							const paginationDetails = this.calculatePages(total, this.size);
							this.pagination = {
								limit: this.size,
								offset: this.page,
								count: this.size * paginationDetails.totalPages,
							};
						},
						error: (err) => console.error("Failed to load local forms:", err),
					});
					this.loaderService.setLoading(false);
				},
			});
	}

	// ---------------- Table events / filters ----------------
	onTableChanged(event: any) {
		const eventName = event.event;

		switch (eventName) {
			case "onPagination":
				const value = event.value;
				this.updateFilters({
					...this.filters,
					page: value.page,
					size: value.limit,
					aocCode: this.roleService.getUserAOC(),
					role: this.roleService.getUserRole(),
				});
				break;
			default:
				break;
		}
	}

	handleDateRangeChange(dateRange: DateRange): void {
		if (dateRange?.enabled === false) {
			this.dateFilterEnabled = false;
			this.updateFilters({
				...this.filters,
				startDate: undefined,
				endDate: undefined,
				page: 1,
			});
			return;
		}

		if (dateRange?.startDate && dateRange?.endDate) {
			this.dateFilterEnabled = true;
			this.startDate = dateRange.startDate;
			this.endDate = dateRange.endDate;
			this.updateFilters({
				...this.filters,
				startDate: moment(dateRange.startDate).format(CONSTANT.DATE_FORMAT.FILTER_DATE_FORMAT),
				endDate: moment(dateRange.endDate).format(CONSTANT.DATE_FORMAT.FILTER_DATE_FORMAT),
				page: 1,
			});
		}
	}

	updateFilters(filters: FormParams) {
		const next: FormParams = {
			...filters,
			role: filters.role ?? this.roleService.getUserRole(),
		};
		if (!filters.startDate) delete next.startDate;
		if (!filters.endDate) delete next.endDate;
		this.filters = next;

		if (!isEmpty(this.filters?.size)) {
			this.configuration.rows = parseInt(`${this.size}`);
		}

		this.updateUrl();
		this.initFormList();
	}

	updateUrl(): void {
		const url = this.router.serializeUrl(
			this.router.createUrlTree([RouteType.FORMS], {
				queryParams: this.filters,
			}),
		);
		// this.location.go(url);
	}

	// ---------------- UI helpers ----------------
	formatDate(dateString: string): string {
		return moment(dateString).format(CONSTANT.DATE_FORMAT.TABLE_DATE_FORMAT);
	}

	createNewForm(): void {
		this.router.navigate([RouteType.NEW_FORM]);
	}

	get constant() {
		return CONSTANT;
	}

	get page(): number {
		return parseInt(`${this.filters?.page ?? this.constant.PAGINATION.DEFAULT_PAGE}`);
	}

	get size(): number {
		return parseInt(`${this.filters?.size ?? this.constant.PAGINATION.DEFAULT_SIZE}`);
	}

	calculatePages(totalDataCount: number, rowsPerPage: number): PaginationDetails {
		const totalPages = Math.ceil(totalDataCount / rowsPerPage);
		const lastPageSize = totalDataCount % rowsPerPage;
		return { totalPages, lastPageSize };
	}

	getSessionConfig(sessionConfig: unknown): string {
		// Array: join
		if (Array.isArray(sessionConfig)) {
			return (sessionConfig as Array<unknown>)
				.filter((v) => v !== null && v !== undefined && `${v}`.trim() !== "")
				.map((v) => `${v}`)
				.join(", ");
		}

		// String: return as-is
		if (typeof sessionConfig === "string") {
			const s = sessionConfig.trim();
			return s.length ? s : "-";
		}

		// Object: try to stringify a *simple* object or flatten values
		if (sessionConfig && typeof sessionConfig === "object") {
			const values = Object.values(sessionConfig as Record<string, unknown>)
				.flatMap((v) => (Array.isArray(v) ? v : [v]))
				.filter((v) => typeof v === "string" && v.trim().length > 0) as string[];
			if (values.length) return values.join(", ");
			try {
				const json = JSON.stringify(sessionConfig);
				return json.length > 2 ? json : "-";
			} catch {
				// fall-through
			}
		}

		// Null/undefined/empty
		return "-";
	}

	formTypeLabel(formType?: { code?: string } | string): string {
		const code = typeof formType === "string" ? formType : formType?.code;
		return getFormTypeShortLabel(code) || "-";
	}

	private withFormTypeLabels(forms: TrainingForms[]): TrainingForms[] {
		return forms.map((form) => ({
			...form,
			formTypeLabel: this.formTypeLabel(form.formType ?? form.formTypeCode),
		}));
	}

	goToRatingForm(rowIndex: number): void {
		this.router.navigate([RouteType.RATINGS, rowIndex]);
	}
}
